/*
Copyright 2025 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { logger } from "matrix-js-sdk/lib/logger";
import {
  EventType,
  type IEvent,
  type MatrixClient,
  MatrixEvent,
  type Room,
} from "matrix-js-sdk";
import { MatrixRTCSession } from "matrix-js-sdk/lib/matrixrtc";

import { callTraceJournal, describeTraceError } from "../telemetry";
import { ElementWidgetActions, widget } from "../widget";
import {
  getRtcMembershipStateKeys,
  type ParsedParticipantIdentity,
} from "./participantIdentity";
import type { RoomMembersHydrationSource } from "./RoomMembersHydrator";
import { throwIfAborted } from "../recovery/abort";

const HYDRATION_COOLDOWN_MS = 5000;

export type RtcMembersHydrationReason =
  | "rtc_member_missing"
  | "participant_unmatched"
  | "manual_reconcile";

export interface RtcMembersHydrationOptions extends ParsedParticipantIdentity {
  forceServer: boolean;
  reason: RtcMembersHydrationReason;
  source: RoomMembersHydrationSource;
  participantIdentity?: string;
}

export interface RtcMembersHydrator {
  ensureRtcMembersHydrated(
    roomId: string,
    options: RtcMembersHydrationOptions,
  ): Promise<boolean>;
}

export interface AuthoritativeRtcMembersHydrator {
  hydrateAllRtcMembers(roomId: string, signal: AbortSignal): Promise<void>;
}

export type RtcMembersHydrationAdapter = RtcMembersHydrator &
  AuthoritativeRtcMembersHydrator;

abstract class BaseRtcMembersHydrator implements RtcMembersHydrator {
  private readonly activeHydrations = new Map<string, Promise<boolean>>();
  private readonly lastAttemptByKey = new Map<string, number>();
  private applicationGeneration = 0;

  public constructor(
    protected readonly client: MatrixClient,
    protected readonly room: Room,
  ) {}

  public async ensureRtcMembersHydrated(
    roomId: string,
    options: RtcMembersHydrationOptions,
  ): Promise<boolean> {
    if (roomId !== this.room.roomId) {
      this.recordSkipped(this.getTracePayload(options), "foreign_room");
      logger.warn("[RtcMembersHydrator] Получен запрос для чужой комнаты", {
        expectedRoomId: this.room.roomId,
        roomId,
      });
      return false;
    }

    if (this.hasRtcMembership(options.userId, options.deviceId)) {
      this.recordSkipped(this.getTracePayload(options), "already_present");
      logger.info(
        "[RtcMembersHydrator] Пропускаем гидрацию, membership уже доступен",
        {
          mode: this.getMode(),
          roomId,
          userId: options.userId,
          deviceId: options.deviceId,
        },
      );
      return true;
    }

    const hydrationKey = `${roomId}|${options.userId}|${options.deviceId}`;
    const activeHydration = this.activeHydrations.get(hydrationKey);
    if (activeHydration) {
      this.recordSkipped(this.getTracePayload(options), "active_hydration");
      logger.info(
        "[RtcMembersHydrator] Пропускаем повторный запрос, job уже активен",
        {
          mode: this.getMode(),
          roomId,
          userId: options.userId,
          deviceId: options.deviceId,
        },
      );
      return activeHydration;
    }

    const lastAttemptTs = this.lastAttemptByKey.get(hydrationKey);
    if (
      typeof lastAttemptTs === "number" &&
      Date.now() - lastAttemptTs < HYDRATION_COOLDOWN_MS
    ) {
      this.recordSkipped(this.getTracePayload(options), "cooldown");
      logger.info("[RtcMembersHydrator] Пропускаем гидрацию из-за cooldown", {
        mode: this.getMode(),
        roomId,
        userId: options.userId,
        deviceId: options.deviceId,
      });
      return false;
    }

    this.lastAttemptByKey.set(hydrationKey, Date.now());
    const generation = this.applicationGeneration;
    const hydration = this.runHydration(roomId, options, generation).finally(
      () => {
        this.activeHydrations.delete(hydrationKey);
      },
    );
    this.activeHydrations.set(hydrationKey, hydration);
    return hydration;
  }

  private async runHydration(
    roomId: string,
    options: RtcMembersHydrationOptions,
    generation: number,
  ): Promise<boolean> {
    const tracePayload = this.getTracePayload(options);
    const startedAt = performance.now();
    const startedEventId = callTraceJournal.record({
      eventType: "matrix.rtc_members_recovery_started",
      stage: "matrix",
      outcome: "started",
      payload: tracePayload,
    });

    logger.info("[RtcMembersHydrator] hydration started", {
      mode: this.getMode(),
      roomId,
      reason: options.reason,
      source: options.source,
      userId: options.userId,
      deviceId: options.deviceId,
      participantIdentity: options.participantIdentity ?? null,
    });

    try {
      await this.hydrateRtcMembers(roomId, options, generation);
      const resolved = this.hasRtcMembership(options.userId, options.deviceId);
      callTraceJournal.record({
        eventType: "matrix.rtc_members_recovery_succeeded",
        stage: "matrix",
        severity: resolved ? "info" : "warn",
        outcome: "succeeded",
        reasonCode: resolved ? undefined : "membership_still_missing",
        relatedEventId: startedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: {
          ...tracePayload,
          resolved,
        },
      });
      logger.info("[RtcMembersHydrator] hydration finished", {
        mode: this.getMode(),
        roomId,
        reason: options.reason,
        source: options.source,
        userId: options.userId,
        deviceId: options.deviceId,
        participantIdentity: options.participantIdentity ?? null,
        resolved,
      });
      return resolved;
    } catch (error) {
      callTraceJournal.record({
        eventType: "matrix.rtc_members_recovery_failed",
        stage: "matrix",
        severity: "error",
        outcome: "failed",
        reasonCode: "rtc_members_recovery_failed",
        relatedEventId: startedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: {
          ...tracePayload,
          error: describeTraceError(error),
        },
      });
      logger.error("[RtcMembersHydrator] hydration failed", {
        mode: this.getMode(),
        roomId,
        reason: options.reason,
        source: options.source,
        userId: options.userId,
        deviceId: options.deviceId,
        participantIdentity: options.participantIdentity ?? null,
      });
      logger.error(error);
      return false;
    }
  }

  private getTracePayload(options: RtcMembersHydrationOptions): {
    mode: "standalone" | "widget";
    reason: RtcMembersHydrationReason;
    forceServer: boolean;
    source: RoomMembersHydrationSource;
    userId: string;
    deviceId: string;
    participantIdentity: string | null;
  } {
    return {
      mode: this.getMode(),
      reason: options.reason,
      forceServer: options.forceServer,
      source: options.source,
      userId: options.userId,
      deviceId: options.deviceId,
      participantIdentity: options.participantIdentity ?? null,
    };
  }

  private recordSkipped(
    tracePayload: {
      mode: "standalone" | "widget";
      reason: RtcMembersHydrationReason;
      forceServer: boolean;
      source: RoomMembersHydrationSource;
      userId: string;
      deviceId: string;
      participantIdentity: string | null;
    },
    skipReason:
      | "cooldown"
      | "already_present"
      | "foreign_room"
      | "active_hydration",
  ): void {
    callTraceJournal.record({
      eventType: "matrix.rtc_members_recovery_skipped",
      stage: "matrix",
      outcome: "cancelled",
      reasonCode: skipReason,
      payload: {
        ...tracePayload,
        skipReason,
      },
    });
  }

  protected applyRtcMembershipEvents(events: MatrixEvent[]): void {
    if (events.length === 0) {
      return;
    }
    this.room.currentState.setStateEvents(events);
  }

  /**
   * Полный pass заменяет локальный набор RTC state, а не merge-ит его.
   * Удаление map необходимо и для пустого ответа сервера.
   */
  protected replaceRtcMembershipEvents(events: MatrixEvent[]): void {
    this.room.currentState.events.delete(EventType.GroupCallMemberPrefix);
    this.applyRtcMembershipEvents(events);
  }

  protected hasRtcMembership(userId: string, deviceId: string): boolean {
    return MatrixRTCSession.callMembershipsForRoom(this.room).some(
      (membership) =>
        membership.sender === userId && membership.deviceId === deviceId,
    );
  }

  protected filterRtcMembershipEvents(
    events: Array<Partial<IEvent>>,
    options: ParsedParticipantIdentity,
  ): MatrixEvent[] {
    const allowedStateKeys = new Set(
      getRtcMembershipStateKeys(options.userId, options.deviceId),
    );

    return events
      .filter(
        (event) =>
          event.type === EventType.GroupCallMemberPrefix &&
          typeof event.state_key === "string" &&
          allowedStateKeys.has(event.state_key),
      )
      .map((event) => new MatrixEvent(event));
  }

  protected filterAllRtcMembershipEvents(
    events: Array<Partial<IEvent>>,
  ): MatrixEvent[] {
    return events
      .filter(
        (event) =>
          event.type === EventType.GroupCallMemberPrefix &&
          typeof event.state_key === "string",
      )
      .map((event) => new MatrixEvent(event));
  }

  protected abstract hydrateRtcMembers(
    roomId: string,
    options: RtcMembersHydrationOptions,
    generation: number,
  ): Promise<void>;

  protected abstract getMode(): "standalone" | "widget";

  /**
   * Инвалидирует apply всех background и authoritative fetches, начатых до
   * текущего full pass на том же экземпляре.
   */
  protected beginAuthoritativeHydration(): number {
    this.applicationGeneration += 1;
    return this.applicationGeneration;
  }

  protected canApplyHydration(generation: number): boolean {
    return generation === this.applicationGeneration;
  }
}

export class StandaloneRtcMembersHydrator
  extends BaseRtcMembersHydrator
  implements RtcMembersHydrator, AuthoritativeRtcMembersHydrator
{
  public async hydrateAllRtcMembers(
    roomId: string,
    signal: AbortSignal,
  ): Promise<void> {
    if (roomId !== this.room.roomId) {
      throw new Error("Authoritative RTC hydration requested for foreign room");
    }
    const generation = this.beginAuthoritativeHydration();
    throwIfAborted(signal);
    const stateEvents = await this.client.roomState(roomId);
    throwIfAborted(signal);
    const memberEvents = this.filterAllRtcMembershipEvents(
      stateEvents as Array<Partial<IEvent>>,
    );
    throwIfAborted(signal);
    if (!this.canApplyHydration(generation)) return;
    this.replaceRtcMembershipEvents(memberEvents);
  }

  protected override async hydrateRtcMembers(
    roomId: string,
    options: RtcMembersHydrationOptions,
    generation: number,
  ): Promise<void> {
    if (!options.forceServer) {
      return;
    }

    // Здесь нужен полный state event, а не только content: MatrixRTC membership
    // рассчитывается из sender/state_key, которые getStateEvent не возвращает.
    const stateEvents = await this.client.roomState(roomId);
    const memberEvents = this.filterRtcMembershipEvents(
      stateEvents as Array<Partial<IEvent>>,
      options,
    );
    if (this.canApplyHydration(generation)) {
      this.applyRtcMembershipEvents(memberEvents);
    }
  }

  protected override getMode(): "standalone" {
    return "standalone";
  }
}

export class WidgetRtcMembersHydrator
  extends BaseRtcMembersHydrator
  implements RtcMembersHydrator, AuthoritativeRtcMembersHydrator
{
  public async hydrateAllRtcMembers(
    roomId: string,
    signal: AbortSignal,
  ): Promise<void> {
    if (roomId !== this.room.roomId) {
      throw new Error("Authoritative RTC hydration requested for foreign room");
    }
    const generation = this.beginAuthoritativeHydration();
    throwIfAborted(signal);
    if (!widget) {
      throw new Error("Widget mode hydration requested without widget API");
    }

    // Существующая host-команда принимает конкретные userId/deviceId и потому
    // непригодна для full pass. Widget API читает весь доступный host state без
    // выдуманного identity и без неполного payload к старому extension action.
    const stateEvents = await widget.api.readStateEvents(
      EventType.GroupCallMemberPrefix,
      undefined,
      undefined,
      [roomId],
    );
    throwIfAborted(signal);
    const memberEvents = this.filterAllRtcMembershipEvents(
      stateEvents as Array<Partial<IEvent>>,
    );
    throwIfAborted(signal);
    if (!this.canApplyHydration(generation)) return;
    this.replaceRtcMembershipEvents(memberEvents);
  }

  protected override async hydrateRtcMembers(
    roomId: string,
    options: RtcMembersHydrationOptions,
    generation: number,
  ): Promise<void> {
    if (!options.forceServer) {
      return;
    }

    if (!widget) {
      throw new Error("Widget mode hydration requested without widget API");
    }

    await widget.api.transport.send(
      ElementWidgetActions.ForceHydrateRtcMember,
      {
        roomId,
        userId: options.userId,
        deviceId: options.deviceId,
      },
    );

    const stateEvents = await widget.api.readStateEvents(
      EventType.GroupCallMemberPrefix,
      undefined,
      undefined,
      [roomId],
    );
    const memberEvents = this.filterRtcMembershipEvents(
      stateEvents as Array<Partial<IEvent>>,
      options,
    );
    if (this.canApplyHydration(generation)) {
      this.applyRtcMembershipEvents(memberEvents);
    }
  }

  protected override getMode(): "widget" {
    return "widget";
  }
}

export function createRtcMembersHydrator(
  client: MatrixClient,
  room: Room,
): RtcMembersHydrationAdapter {
  if (widget) {
    return new WidgetRtcMembersHydrator(client, room);
  }

  return new StandaloneRtcMembersHydrator(client, room);
}
